import { motion } from 'framer-motion'
import { Building2, UserRound } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import type { CompanyHit, PersonHit, SnapshotSearchResponse } from '@/services/api/registers'
import { cn } from '@/utils/cn'

const list = { hidden: {}, show: { transition: { staggerChildren: 0.035 } } }
const row = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0, transition: { duration: 0.22 } },
}

function Period({ since, until }: { since: string | null; until: string | null }) {
  const { t } = useTranslation()
  if (!since && !until) return null
  return (
    <span
      className={cn(
        'font-mono text-2xs tabular-nums',
        until ? 'text-muted-foreground line-through decoration-border' : 'text-muted-foreground'
      )}
    >
      {since ?? '…'} → {until ?? t('extensions.osint.search.current')}
    </span>
  )
}

/**
 * One result card. Plain in the console, a selectable button in the picker —
 * the row is the pick target there, so the whole card has to be focusable and
 * keyboard-operable rather than carrying a separate "choose" affordance.
 */
function PickableRow({
  children,
  onPick,
  picked,
}: {
  children: ReactNode
  onPick?: () => void
  picked?: boolean
}) {
  const base = cn(
    'rounded-lg border bg-card p-4',
    picked ? 'border-primary ring-1 ring-primary' : 'border-border'
  )
  if (!onPick) {
    return (
      <motion.li variants={row} className={base}>
        {children}
      </motion.li>
    )
  }
  return (
    <motion.li variants={row}>
      <button
        type="button"
        onClick={onPick}
        aria-pressed={picked}
        className={cn(
          base,
          'block w-full cursor-pointer text-left transition-colors',
          'hover:border-primary/60 hover:bg-accent/40',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        )}
      >
        {children}
      </button>
    </motion.li>
  )
}

function CompanyRow({
  hit,
  onPick,
  picked,
}: {
  hit: CompanyHit
  onPick?: (hit: CompanyHit) => void
  picked?: boolean
}) {
  const { t } = useTranslation()
  const current = hit.officers.filter(o => !o.until)
  const ended = hit.officers.length - current.length
  return (
    <PickableRow picked={picked} onPick={onPick && (() => onPick(hit))}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <div className="flex min-w-0 items-start gap-2">
          <Building2 className="mt-0.5 size-4 shrink-0 text-primary" />
          <div className="min-w-0">
            <h4 className="text-sm font-semibold leading-snug text-foreground">{hit.label}</h4>
            <p className="text-xs text-muted-foreground">{hit.address}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="outline" className="font-mono">
            IČO {hit.ico}
          </Badge>
          {hit.type && <Badge variant="muted">{hit.type}</Badge>}
          {hit.dateEnd ? (
            <Badge variant="destructive">
              {t('extensions.osint.search.dissolved', { on: hit.dateEnd })}
            </Badge>
          ) : hit.dateStart ? (
            <span className="font-mono text-2xs text-muted-foreground">
              {t('extensions.osint.search.since', { on: hit.dateStart })}
            </span>
          ) : null}
        </div>
      </div>
      {hit.business.length > 0 && (
        <p className="mt-2 line-clamp-2 text-2xs text-muted-foreground">
          {hit.business.join(' · ')}
        </p>
      )}
      {hit.officers.length > 0 && (
        <div className="mt-3 border-t border-border/60 pt-3">
          <div className="mb-1.5 text-2xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
            {t('extensions.osint.search.officers', { n: hit.officersTotal })}
          </div>
          <ul className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
            {hit.officers.map(o => (
              <li
                key={`${o.partyId}-${o.role}-${o.since ?? ''}`}
                className="flex flex-wrap items-baseline gap-x-2 text-xs"
              >
                {o.kind === 'person' ? (
                  <UserRound className="size-3 translate-y-0.5 text-muted-foreground" />
                ) : (
                  <Building2 className="size-3 translate-y-0.5 text-muted-foreground" />
                )}
                <span className={cn('text-foreground', o.until && 'text-muted-foreground')}>
                  {o.name}
                </span>
                <span className="text-muted-foreground">{o.role}</span>
                {o.birthDate && (
                  <span className="font-mono text-2xs text-muted-foreground">*{o.birthDate}</span>
                )}
                <Period since={o.since} until={o.until} />
              </li>
            ))}
          </ul>
          {ended > 0 && (
            <p className="mt-1 text-2xs text-muted-foreground">
              {t('extensions.osint.search.endedNote', { n: ended })}
            </p>
          )}
        </div>
      )}
    </PickableRow>
  )
}

function PersonRow({
  hit,
  onOpenCompany,
  onPick,
  picked,
}: {
  hit: PersonHit
  onOpenCompany?: (companyLabel: string) => void
  onPick?: (hit: PersonHit) => void
  picked?: boolean
}) {
  const { t } = useTranslation()
  return (
    <PickableRow picked={picked} onPick={onPick && (() => onPick(hit))}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <div className="flex min-w-0 items-start gap-2">
          <UserRound className="mt-0.5 size-4 shrink-0 text-primary" />
          <div className="min-w-0">
            <h4 className="text-sm font-semibold leading-snug text-foreground">{hit.label}</h4>
            <p className="text-xs text-muted-foreground">
              {hit.address}
              {hit.birthDate && <span className="ml-2 font-mono text-2xs">*{hit.birthDate}</span>}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {hit.identityBasis && (
            <Badge
              variant="muted"
              className="font-mono text-2xs"
              title={t('extensions.osint.search.identityHint')}
            >
              {hit.identityBasis}
            </Badge>
          )}
          <Badge variant="outline">
            {t('extensions.osint.search.companies', { n: hit.engagementsTotal })}
          </Badge>
        </div>
      </div>
      {hit.engagements.length > 0 && (
        <ul className="mt-3 divide-y divide-border/60 border-t border-border/60">
          {hit.engagements.map(e => (
            <li
              key={`${e.companyId}-${e.role}-${e.since ?? ''}`}
              className="flex flex-wrap items-baseline gap-x-2 py-1.5 text-xs"
            >
              {e.company && onOpenCompany ? (
                <button
                  type="button"
                  onClick={() => onOpenCompany(e.company ?? '')}
                  className={cn(
                    'text-left text-foreground underline-offset-2 hover:text-primary hover:underline',
                    e.until && 'text-muted-foreground'
                  )}
                >
                  {e.company}
                </button>
              ) : e.company ? (
                <span className={cn('text-foreground', e.until && 'text-muted-foreground')}>
                  {e.company}
                </span>
              ) : (
                <span className="font-mono text-muted-foreground">{e.companyId}</span>
              )}
              {e.ico && <span className="font-mono text-2xs text-muted-foreground">{e.ico}</span>}
              <span className="text-muted-foreground">{e.role}</span>
              <Period since={e.since} until={e.until} />
            </li>
          ))}
        </ul>
      )}
    </PickableRow>
  )
}

/**
 * One titled, staggered list of hits.
 *
 * `resultKey` re-mounts the list per result set: rows mounted under an
 * already-revealed motion parent would otherwise keep the inherited `hidden`
 * state (opacity 0).
 */
function ResultSection({
  heading,
  resultKey,
  children,
}: {
  heading: string
  resultKey: string
  children: ReactNode
}) {
  return (
    <section>
      <h3 className="mb-2 text-2xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {heading}
      </h3>
      <motion.ul
        key={resultKey}
        variants={list}
        initial="hidden"
        animate="show"
        className="space-y-3"
      >
        {children}
      </motion.ul>
    </section>
  )
}

/** The register document the analyst picked, in the shape the import needs. */
export interface RegisterPick {
  id: string
  label: string
  kind: 'person' | 'company'
}

export interface SnapshotSearchResultsProps {
  data: SnapshotSearchResponse
  /** Clicking a company inside a person's engagements re-runs the search for that company. */
  onOpenCompany?: (companyLabel: string) => void
  /** Turns every row into a pick target. Omitted in the read-only console. */
  onPick?: (pick: RegisterPick) => void
  /** Register id of the currently picked row, if any. */
  pickedId?: string | null
  /** Renders one column instead of two — for narrow surfaces like the picker. */
  stacked?: boolean
  className?: string
}

/** Result lists for the snapshot search console — companies with officers, persons with their companies. */
export function SnapshotSearchResults({
  data,
  onOpenCompany,
  onPick,
  pickedId,
  stacked,
  className,
}: SnapshotSearchResultsProps) {
  const { t } = useTranslation()
  const empty = data.companies.length === 0 && data.persons.length === 0
  const resultKey = `${data.country}:${data.kind}:${data.query}:${data.page}`
  if (empty) {
    return (
      <p className={cn('text-sm text-muted-foreground', className)}>
        {t('extensions.osint.search.noResults', { q: data.query })}
      </p>
    )
  }
  return (
    <div className={cn('grid gap-6', stacked ? 'grid-cols-1' : 'lg:grid-cols-2', className)}>
      {data.kind !== 'person' || data.companies.length > 0 ? (
        <ResultSection
          resultKey={resultKey}
          heading={t('extensions.osint.search.companiesHeading', {
            shown: data.companies.length,
            found: data.found.companies,
          })}
        >
          {data.companies.map(hit => (
            <CompanyRow
              key={hit.id}
              hit={hit}
              picked={pickedId === hit.id}
              onPick={
                onPick &&
                (picked => onPick({ id: picked.id, label: picked.label, kind: 'company' }))
              }
            />
          ))}
        </ResultSection>
      ) : null}
      {data.kind !== 'company' || data.persons.length > 0 ? (
        <ResultSection
          resultKey={resultKey}
          heading={t('extensions.osint.search.personsHeading', {
            shown: data.persons.length,
            found: data.found.persons,
          })}
        >
          {data.persons.map(hit => (
            <PersonRow
              key={hit.id}
              hit={hit}
              onOpenCompany={onOpenCompany}
              picked={pickedId === hit.id}
              onPick={
                onPick && (picked => onPick({ id: picked.id, label: picked.label, kind: 'person' }))
              }
            />
          ))}
        </ResultSection>
      ) : null}
    </div>
  )
}
